import * as React from 'react';
import { useMemo, useState } from 'react';

import { QuestionTypes } from '@showbie-socrative/socrative-utils';
import { max } from 'date-fns';

import { Spinner } from '~shared/components/Spinner';
import { toast } from '~shared/components/Toast';
import { handleAiRateLimitError } from '~teacher/components/QuizEditor/errorHandlers/aiRateLimitErrorHandler';
import { calculateStudentTotalScore } from '~teacher/components/StudentScore/utils';
import {
  useActivityByInstanceId,
  useQuizByActivityId,
  useQuizLimit,
} from '~teacher/hook';
import { useGetActivityInsights } from '~teacher/hook/activity/insights/useGetActivityInsights';
import { useGenerateQuestionsByStudents } from '~teacher/hook/ai/useGenerateQuestionsByStudents';
import { QuestionScore } from '~teacher/models/activities';

import { ActivityInsights } from '../../../../../../api/activities/insights/insightSchema';
import { personalQuizUrl } from '../../../../../../api/urls';
import {
  SelectQuestions,
  SelectQuestionsLoading,
} from '../AreasOfImprovement/SelectQuestions';
import { Area, OnGenerate, SelectStudents, StudentRow } from './SelectStudents';

export type SpecificStudentStep =
  | 'step1-select-student'
  | 'step2-select-questions';

function checkIfInsightsAreOutdated(
  insights: ActivityInsights,
  questionScores: Array<QuestionScore>
): boolean {
  const latestInsightsDate = new Date(insights.updatedDate);

  const timestamps = questionScores.flatMap((questionScore) =>
    Object.values(questionScore.studentScores ?? {}).map(
      (studentScore) => new Date(studentScore.updatedDate)
    )
  );

  const latestGradingDate = max(timestamps);

  // If the latest grading date is after the latest insights date, insights are outdated
  return latestGradingDate > latestInsightsDate;
}

interface SpecificStudentProps {
  backToGenerationType?: () => void;
  activityInstanceId: number;
  onClose: () => void;
}

export function SpecificStudent({
  backToGenerationType,
  activityInstanceId,
  onClose,
}: SpecificStudentProps) {
  type SelectQuestion = {
    id: string;
    questionType: QuestionTypes;
    text: string;
    explanation?: string;
    answers?: Array<{ order: number; text: string; isCorrect: boolean }>;
  };
  const [step, setStep] = useState<SpecificStudentStep>('step1-select-student');
  const [generatedQuestions, setGeneratedQuestions] = useState<
    SelectQuestion[]
  >([]);
  const [questionCount, setQuestionCount] = useState(5);
  const generateQuestionsMutation = useGenerateQuestionsByStudents();
  const quizLimitQuery = useQuizLimit();

  const activityResult = useActivityByInstanceId({
    activityInstanceId,
  });

  const quizByActivityQuery = useQuizByActivityId({
    activityInstanceId: activityInstanceId,
    quizUrl: personalQuizUrl,
  });

  const activityInsightsQuery = useGetActivityInsights({
    activityInstanceId,
  });

  const handleGenerate: OnGenerate = ({
    selectedStudentIds,
    questionCount,
    generateExplanations,
    numAreasOfImprovement,
  }) => {
    if (!activityResult.data) return;

    // Map student IDs to UUIDs for the API
    const studentUuids = selectedStudentIds
      .map(
        (id) =>
          Object.values(activityResult.data!.students).find((s) => s.id === id)
            ?.userUuid
      )
      .filter((uuid): uuid is string => !!uuid);

    if (studentUuids.length === 0) {
      toast.critical('No valid students selected.');
      return;
    }

    setQuestionCount(questionCount);
    setStep('step2-select-questions');

    generateQuestionsMutation.mutate(
      {
        numAreasOfImprovement,
        activityInstanceId,
        studentIds: studentUuids,
        questionCount: questionCount,
        generateExplanations: generateExplanations,
      },
      {
        onSuccess: (data) => {
          setGeneratedQuestions(
            data.questions.map((q, i) => ({
              id: `generated-${i}`,
              questionType: q.questionType,
              text: q.questionText,
              explanation: q.explanation,
              answers: q.answers?.map((a) => ({
                order: a.order!,
                text: a.text!,
                isCorrect: a.isCorrect!,
              })),
            }))
          );
        },
        onError: (error: unknown) => {
          setStep('step1-select-student');
          handleAiRateLimitError(error);
        },
      }
    );
  };

  const isInsightsOutdated = useMemo(() => {
    if (!activityResult.data || !activityInsightsQuery.data) return false;
    return checkIfInsightsAreOutdated(
      activityInsightsQuery.data,
      Object.values(activityResult.data.questionScores)
    );
  }, [activityResult.data, activityInsightsQuery.data]);

  const studentsData = useMemo<StudentRow[]>(() => {
    if (
      !activityResult.data ||
      !activityInsightsQuery.data ||
      !quizByActivityQuery.data
    )
      return [];

    /*
     * Extract data from the activity result since question scores only contain manual scores
     * Responses will contain autograded responses
     */
    const { students, questionScores, responses } = activityResult.data;
    const insights = activityInsightsQuery.data.insights.insights;

    /* Get question IDs from the quiz to calculate max score */
    const quizQuestions = quizByActivityQuery.data.quizQuestion;
    const questionIds = Object.keys(quizQuestions).map(Number);

    const rows = Object.values(students).map((student) => {
      /* Calculate student total score using shared utility
       * Handles both manual scores and autograded responses
       */
      const {
        score: currentScore,
        maxScore,
        percentage: scorePercentage,
      } = calculateStudentTotalScore({
        studentUserUuid: student.userUuid,
        studentId: student.id,
        questionIds,
        questionScores,
        responses,
      });

      const hasResponses = Object.values(responses).some(
        (response) => response.userUuid === student.userUuid
      );
      const isAbsent = !student.isFinished && !hasResponses;

      const studentAreas: Area[] = insights.map((insight) => {
        const isImprovementNeeded =
          isAbsent || insight.studentIds.includes(student.userUuid);
        return {
          index: insight.index,
          name: insight.topic,
          description: insight.description,
          isImprovementNeeded,
        };
      });

      studentAreas.sort((a, b) => a.index - b.index);

      return {
        id: student.id,
        name: student.name,
        areas: studentAreas,
        score: {
          text: isAbsent ? 'Absent' : `${currentScore} of ${maxScore}`,
          currentScore,
          maxScore,
          isAbsent: !!isAbsent,
          percentage: isAbsent ? undefined : scorePercentage,
        },
        // Used by TanStack Table for sorting
        _rawScore: currentScore,
        _isAbsent: !!isAbsent,
      };
    });

    // Sorting is handled by TanStack Table in SelectStudents
    return rows;
  }, [
    activityResult.data,
    activityInsightsQuery.data,
    quizByActivityQuery.data,
  ]);

  if (
    activityResult.isLoading ||
    activityInsightsQuery.isLoading ||
    quizByActivityQuery.isLoading
  ) {
    return (
      <div className="flex justify-center items-center h-full">
        <Spinner />
      </div>
    );
  }

  if (step === 'step2-select-questions') {
    if (generateQuestionsMutation.isLoading || quizLimitQuery.isLoading) {
      return (
        <SelectQuestionsLoading
          onBack={() => setStep('step1-select-student')}
          questionCount={questionCount}
        />
      );
    }
    return (
      <SelectQuestions
        source="specific_student"
        onBack={() => setStep('step1-select-student')}
        onClose={onClose}
        quizName={quizByActivityQuery.data?.quiz.name ?? ''}
        questions={generatedQuestions}
        currentQuizCount={quizLimitQuery.data?.currentQuizCount ?? 0}
        limit={quizLimitQuery.data?.limit ?? Infinity}
        aiDetails={{
          promptVersion: generateQuestionsMutation.data?.promptVersion,
          insightId: generateQuestionsMutation.data?.insightId,
        }}
      />
    );
  }

  return (
    <SelectStudents
      backToGenerationType={backToGenerationType}
      students={studentsData}
      onGenerate={handleGenerate}
      isGenerating={generateQuestionsMutation.isLoading}
      isInsightsOutdated={isInsightsOutdated}
    />
  );
}
